import React, { useState, useEffect } from 'react';
import { useSearchContactsQuery } from '../../store/services/whatsappApi';
import { debounce } from 'lodash';
import ContactSearchFilters, { SearchField } from './ContactSearchFilters';

interface ContactSearchProps {
  onSearchResults: (results: any[]) => void;
  onSearching: (isSearching: boolean) => void;
  onSearchActive: (isActive: boolean) => void;
}

const ContactSearch: React.FC<ContactSearchProps> = ({ onSearchResults, onSearching, onSearchActive }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<SearchField>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50); // Show 50 results per page for better performance
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [optInStatus, setOptInStatus] = useState<boolean | undefined>(undefined);
  const [tags, setTags] = useState<string>('');
  const [createdAfter, setCreatedAfter] = useState<string>('');
  const [createdBefore, setCreatedBefore] = useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Build query parameters
  const queryParams = {
    query: debouncedSearchTerm,
    field: activeFilter !== 'all' ? activeFilter : undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    opt_in_status: optInStatus,
    tags: tags || undefined,
    created_after: createdAfter || undefined,
    created_before: createdBefore || undefined,
    skip: (currentPage - 1) * pageSize,
    limit: pageSize
  };

  // Skip the query if search term is empty
  const { data: searchResults, isFetching } = useSearchContactsQuery(queryParams, {
    skip: !debouncedSearchTerm,
  });

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    handler();
    
    return () => {
      handler.cancel();
    };
  }, [searchTerm]);

  // Pass search results to parent component
  useEffect(() => {
    if (searchResults) {
      onSearchResults(searchResults.contacts);
    } else if (!debouncedSearchTerm) {
      // Clear results when search term is empty
      onSearchResults([]);
    }

    // Notify parent about search status
    onSearching(isFetching);
    onSearchActive(!!debouncedSearchTerm);
  }, [searchResults, isFetching, debouncedSearchTerm, onSearchResults, onSearching, onSearchActive]);

  // Show no results message
  const showNoResults = debouncedSearchTerm && !isFetching && searchResults && searchResults.contacts.length === 0;

  // Reset to first page when search term or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, activeFilter]);

  return (
    <div className="relative">
      <div className="flex items-center bg-white rounded-md border border-gray-300 focus-within:border-whatsapp-green focus-within:ring-1 focus-within:ring-whatsapp-green">
        <div className="pl-3 pr-2">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder={`Search contacts by ${activeFilter === 'all' ? 'name, phone, email...' : activeFilter}`}
          className="w-full py-2 px-1 outline-none text-sm"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
         {isFetching && (
           <div className="pr-3 flex items-center">
             <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-whatsapp-green"></div>
             <span className="ml-2 text-sm text-gray-500">Searching...</span>
           </div>
         )}
        {searchTerm && !isFetching && (
          <button 
            className="pr-3 text-gray-400 hover:text-gray-600"
            onClick={() => setSearchTerm('')}
          >
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
       {/* Search filters */}
       <ContactSearchFilters
         activeFilter={activeFilter}
         onFilterChange={(filter) => {
           setActiveFilter(filter);
           // Re-trigger search with new filter if there's a search term
           if (searchTerm) {
             setDebouncedSearchTerm(searchTerm);
           }
         }}
       />

       {/* Sorting and Advanced Filters */}
       {debouncedSearchTerm && (
         <div className="mt-2">
           <div className="flex items-center gap-4 mb-2">
             <div className="flex items-center gap-2">
               <label className="text-sm text-gray-600">Sort by:</label>
               <select
                 value={sortBy}
                 onChange={(e) => setSortBy(e.target.value)}
                 className="text-sm border border-gray-300 rounded px-2 py-1"
               >
                 <option value="created_at">Date Created</option>
                 <option value="name">Name</option>
                 <option value="phone_number">Phone</option>
                 <option value="email">Email</option>
               </select>
             </div>
             <div className="flex items-center gap-2">
               <label className="text-sm text-gray-600">Order:</label>
               <select
                 value={sortOrder}
                 onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                 className="text-sm border border-gray-300 rounded px-2 py-1"
               >
                 <option value="desc">Newest First</option>
                 <option value="asc">Oldest First</option>
               </select>
             </div>
             <button
               onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
               className="text-sm text-whatsapp-green hover:text-whatsapp-green-dark underline"
             >
               {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
             </button>
           </div>

           {/* Advanced Filters */}
           {showAdvancedFilters && (
             <div className="bg-gray-50 p-3 rounded-md border">
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                 <div>
                   <label className="block text-sm text-gray-600 mb-1">Opt-in Status</label>
                   <select
                     value={optInStatus === undefined ? '' : String(optInStatus)}
                     onChange={(e) => setOptInStatus(e.target.value === '' ? undefined : e.target.value === 'true')}
                     className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                   >
                     <option value="">All</option>
                     <option value="true">Opted In</option>
                     <option value="false">Not Opted In</option>
                   </select>
                 </div>
                 <div>
                   <label className="block text-sm text-gray-600 mb-1">Tags (comma-separated)</label>
                   <input
                     type="text"
                     value={tags}
                     onChange={(e) => setTags(e.target.value)}
                     placeholder="e.g. vip, customer"
                     className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                   />
                 </div>
                 <div>
                   <label className="block text-sm text-gray-600 mb-1">Created After</label>
                   <input
                     type="date"
                     value={createdAfter}
                     onChange={(e) => setCreatedAfter(e.target.value)}
                     className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                   />
                 </div>
                 <div>
                   <label className="block text-sm text-gray-600 mb-1">Created Before</label>
                   <input
                     type="date"
                     value={createdBefore}
                     onChange={(e) => setCreatedBefore(e.target.value)}
                     className="w-full text-sm border border-gray-300 rounded px-2 py-1"
                   />
                 </div>
               </div>
             </div>
           )}
         </div>
       )}

       {/* Search Results Summary */}
       {searchResults && searchResults.total > 0 && (
         <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
           <div>
             Found {searchResults.total} result{searchResults.total !== 1 ? 's' : ''} for "{debouncedSearchTerm}"
           </div>
           {searchResults.total > pageSize && (
             <div>
               Page {currentPage} of {Math.ceil(searchResults.total / pageSize)}
             </div>
           )}
         </div>
       )}

       {/* No Results Message */}
       {showNoResults && (
         <div className="text-center py-8 text-gray-500">
           <div className="text-lg mb-2">No contacts found</div>
           <div className="text-sm">Try adjusting your search terms or filters</div>
         </div>
       )}

       {/* Pagination controls */}
       {searchResults && searchResults.total > pageSize && (
         <div className="flex items-center justify-between mt-4 px-2">
           <div className="text-sm text-gray-600">
             Showing {searchResults.skip + 1}-{Math.min(searchResults.skip + searchResults.contacts.length, searchResults.total)} of {searchResults.total} results
           </div>
           <div className="flex space-x-2">
             <button
               onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
               disabled={currentPage === 1}
               className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
             >
               Previous
             </button>
             <span className="px-3 py-1 text-sm text-gray-700">
               Page {currentPage} of {Math.ceil(searchResults.total / pageSize)}
             </span>
             <button
               onClick={() => setCurrentPage(prev => prev + 1)}
               disabled={currentPage >= Math.ceil(searchResults.total / pageSize)}
               className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
             >
               Next
             </button>
           </div>
         </div>
       )}
     </div>
  );
};

export default ContactSearch;
